import { useState } from "react";
import { Form, Link, useActionData, useRouteLoaderData, useSubmit } from "react-router-dom";

import UserInfo from "../common/userInfo";
import type { Assignment } from "./types";
import { SignatureField } from "../forms/records/form/SignatureField";

type TimeJobTagData = {
    message?: string;
    [key: string]: unknown;
};

function getDate(value: string | undefined) {
    return value?.slice(0, 10) ?? "";
}
/*
function getTime(value: string | undefined, fallback: string) {
    return value?.slice(11, 16) || fallback;
}
*/
export default function JobTagForm() {
    const assignment = useRouteLoaderData("assignment") as Assignment;
    const actionData = useActionData() as TimeJobTagData | undefined;
    const submit = useSubmit();

    const startDate = getDate(assignment.startTime);
    const endDate = getDate(assignment.endTime);
    // const startTime = getTime(assignment.startTime, "00:00");
    // const endTime = getTime(assignment.endTime, "00:00");
    const [formValues, setFormValues] = useState(assignment);

    const toQuarterHourLocalDateTime = (value: string | undefined, fallback: string) => {
        const normalized = value ? value.replace(" ", "T") : fallback;
        const date = new Date(normalized);
        if (Number.isNaN(date.getTime())) return fallback;
        const minutes = date.getMinutes();
        date.setMinutes(Math.floor(minutes / 15) * 15, 0, 0);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const mins = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${mins}`;
    };

    const handleFillTestData = () => {
        const travelStartTime = toQuarterHourLocalDateTime(assignment.startTime, `${startDate}T07:00`);
        const workStartTime = toQuarterHourLocalDateTime(assignment.startTime, `${startDate}T07:30`);
        const workEndTime = toQuarterHourLocalDateTime(assignment.endTime, `${endDate}T15:30`);
        const travelEndTime = toQuarterHourLocalDateTime(assignment.endTime, `${endDate}T16:00`);

        setFormValues(current => ({
            ...current,
            coords: current.coords || "37.7749,-122.4194,5.00",
            travelStartTime,
            workStartTime,
            hadLunch: "yes",
            workEndTime,
            travelEndTime,
            workFinished: "yes",
            workRequired: "Complete remaining assigned scope and confirm site is ready for turnover.",
            workPerformed: "Performed scheduled field work, documented findings, and cleaned up the work area.",
            additionalInfo: "Generated test data for validation."
        }));
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        formData.set("action", "Submitted");
        submit(formData, { method: "post" });
    };

    const handleSave = (form: HTMLFormElement | null) => {
        if (!form) return;
        const formData = new FormData(form);
        formData.set("action", "Saved");
        submit(formData, { method: "post" });
    };

    // 2026-04-23T11:13

    return (
        <Form onSubmit={handleFormSubmit}>
            <input type="hidden" name="assignmentId" value={formValues.id} />
            <input
                className = "nodisplay" readOnly
                name = "coords" value = {formValues.coords}
            />
            <div className="cssSeperateWhenWide ps-3 pt-3">
                <div className="flex-grow-1 me-3">
                    <div className="mb-3">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleFillTestData}
                        >
                            Fill Test Data
                        </button>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Project:</span>
                        <span>{assignment.projectName ?? ""}</span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Project Manager:</span>
                        <UserInfo id={assignment.projectManagerId} name={assignment.projectManagerName} />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Client PO Number:</span>
                        <span>{assignment.clientPONumber ?? ""}</span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Project Category:</span>
                        <span>{[assignment.projectCategory, assignment.projectSubCategory].filter(Boolean).join(" - ")}</span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Work Category:</span>
                        <span>{[assignment.workCategory, assignment.workSubCategory].filter(Boolean).join(" - ")}</span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Assignemnt Category:</span>
                        <span>{assignment.laborCategory}</span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Location:</span>
                        <span>{assignment.location ?? ""}</span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Scheduled:</span>
                        <span>{assignment.allDay === "yes" ? assignment.startTime.slice(0, 10) + " ~ " +  assignment.endTime.slice(0, 10): assignment.startTime + " ~ "+ assignment.endTime}</span>
                    </div>
                    {assignment.siteContactId && <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Site Contact:</span>
                        <span>
                            <Link to={`/Contacts/${assignment.siteContactId}`}>
                                {[assignment.siteContactName, assignment.siteContactPhone].filter(Boolean).join(" - ")}
                            </Link>
                        </span>
                    </div>}
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold">Supervisor Email:</span>
                        <span><UserInfo id={assignment.supervisorId} name={assignment.supervisorName}/></span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Pre Work Driver:</span>
                        <span>{assignment.preDriver}</span>
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <span className="fw-bold me-2">Post Work Driver:</span>
                        <span>{assignment.postDriver}</span>
                    </div>
                    {assignment.jobTagLocation === "yes" ? (
                        <div className="pt-3">
                            <div className="border rounded px-3 py-3 d-flex align-items-start justify-content-between gap-3 flex-wrap">
                                <div className="flex-grow-1">
                                    <div className="fw-bold">Location Capture</div>
                                    <div className="text-muted small">
                                        Capture your current coordinates for this job tag.
                                    </div>
                                    {formValues.coords ? (
                                        <div className="mt-2">
                                            <div className="small text-muted">Current Coordinates</div>
                                            <a
                                                href={`https://www.google.com/maps/search/${formValues.coords.split(",").slice(0, 2).join(",")}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="d-inline-block text-decoration-none mt-1"
                                            >
                                                {formValues.coords.split(",").slice(0, 2).join(",")}
                                            </a>
                                            <div className="small text-muted">
                                                Accuracy: {formValues.coords.split(",")[2]} meters
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="small text-muted mt-2">No location captured yet.</div>
                                    )}
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            if(navigator.geolocation){
                                                navigator.geolocation.getCurrentPosition(pos => {
                                                    setFormValues({
                                                        ...formValues,
                                                        coords: pos.coords.latitude + "," + pos.coords.longitude + "," + Number(pos.coords.accuracy).toFixed(2)
                                                    });
                                                }, error => window.alert(error.message), {enableHighAccuracy: true, timeout: 5000});
                                            }else{
                                                window.alert("The function is not supported.");
                                                setFormValues({ ...formValues, coords: "" });
                                            }
                                        }}
                                    >
                                        {formValues.coords ? "Refresh Location" : "Get Location"}
                                    </button>
                                    {formValues.coords ? (
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setFormValues({ ...formValues, coords: "" })}
                                        >
                                            Clear
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="travelStartTime">Travel Start:</label>
                        <input
                            required
                            type="datetime-local"
                            min = {`${startDate}T00:00`}
                            max = {formValues.travelEndTime ?? `${endDate}T23:59`}
                            step= {assignment.accurateTime?`60`: `900`}
                            id="travelStartTime"
                            name="travelStartTime"
                            className="form-control flex-grow-1 ms-2"
                            value={formValues.travelStartTime}
                            onChange={event => setFormValues({ ...formValues, travelStartTime: event.currentTarget.value })}
                        />
                        <label className="fw-bold text-nowrap ms-3" htmlFor="workStartTime">Work Start:</label>
                        <input
                            required
                            type="datetime-local"
                            min = {formValues.travelStartTime ?? `${startDate}T00:00`}
                            max = {formValues.workEndTime ?? `${endDate}T23:59`}
                            step = {assignment.accurateTime?`60`: `900`}
                            id="workStartTime"
                            name="workStartTime"
                            className="form-control flex-grow-1 ms-2"
                            value={formValues.workStartTime}
                            onChange={event => setFormValues({ ...formValues, workStartTime: event.currentTarget.value })}
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="hadLunch">Had Lunch:</label>
                        <select
                            id = "hadLunch"
                            name = "hadLunch"
                            required
                            className="form-control ms-2"
                            value={formValues.hadLunch}
                            onChange={event => setFormValues({ ...formValues, hadLunch: event.currentTarget.value })}
                        >
                            <option value="">Select</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="workEndTime">Work End:</label>
                        <input
                            required
                            type="datetime-local"
                            min = {formValues.workStartTime ?? `${startDate}T00:00`}
                            max = {formValues.travelEndTime ?? `${endDate}T23:59`}
                            step= {assignment.accurateTime?`60`: `900`}
                            id="workEndTime"
                            name= "workEndTime"
                            className="form-control flex-grow-1 ms-2"
                            value={formValues.workEndTime}
                            onChange={event => setFormValues({ ...formValues, workEndTime: event.currentTarget.value })}
                        />
                        <label className="fw-bold text-nowrap ms-3" htmlFor="travelEndTime">Travel End:</label>
                        <input
                            required
                            type="datetime-local"
                            min = {formValues.workEndTime ?? `${startDate}T00:00`}
                            max = {`${endDate}T23:59`}
                            step= {assignment.accurateTime?`60`: `900`}
                            id="travelEndTime"
                            name="travelEndTime"
                            className="form-control flex-grow-1 ms-2"
                            value={formValues.travelEndTime}
                            onChange={event => setFormValues({ ...formValues, travelEndTime: event.currentTarget.value })}
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="workFinished">Work Finished:</label>
                        <select
                            id="workFinished"
                            name="workFinished"
                            required
                            className="form-control ms-2"
                            value={formValues.workFinished}
                            onChange={event => setFormValues({ ...formValues, workFinished: event.currentTarget.value })}
                        >
                            <option value="">Select</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                            <option value="unknown">Unknown</option>
                        </select>
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="workRequired">Work Required:</label>
                        <textarea
                            id="workRequired"
                            name="workRequired"
                            className="form-control flex-grow-1 ms-3"
                            rows={5}
                            value={formValues.workRequired}
                            onChange={event => setFormValues({ ...formValues, workRequired: event.currentTarget.value })}
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="workPerformed">Work Performed:</label>
                        <textarea
                            id="workPerformed"
                            name="workPerformed"
                            className="form-control flex-grow-1 ms-3"
                            rows={5}
                            value={formValues.workPerformed}
                            onChange={event => setFormValues({ ...formValues, workPerformed: event.currentTarget.value })}
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap" htmlFor="additionalInfo">Additional Information:</label>
                        <textarea
                            id="additionalInfo"
                            name="additionalInfo"
                            className="form-control flex-grow-1 ms-3"
                            rows={5}
                            value={formValues.additionalInfo}
                            onChange={event => setFormValues({ ...formValues, additionalInfo: event.currentTarget.value })}
                        />
                    </div>

                    <div className="mt-3">
                        <span className="fw-bold d-block mb-2 required-field">Signature</span>
                        <SignatureField
                            value={formValues.techSign}
                            onChange={(next: string) => setFormValues({ ...formValues, techSign: next })}
                            required={true}
                        />
                        <input type="hidden" name="techSign" value={formValues.techSign} />
                    </div>

                    {actionData?.message ? (
                        <div className="alert alert-success mt-3 mb-0">{actionData.message}</div>
                    ) : null}
                </div>
            </div>

            <div className="my-3 d-flex align-items-center">
                <button
                    type="button"
                    className="btn btn-success me-2"
                    onClick={event => handleSave(event.currentTarget.form)}
                >
                    Save
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    Submit
                </button>
                <Link children="Back" to=".." className="btn btn-outline-secondary ms-auto" />
            </div>
        </Form>
    );
}
